import { differenceInDays, parseISO } from 'date-fns';
import { Cruise } from '../context/CruiseContext';

export interface LoyaltyTier {
  name: string;
  min_value: number;
  max_value: number | null;
}

export interface LoyaltyCalculationRules {
  method: 'days' | 'cabinTypeMultiplier' | 'manual' | 'sailings';
  pointsPerDay?: number;
  cabinTypeMultipliers?: { [cabinType: string]: number };
  forceManualInput?: boolean;
  /** Secondary sailing-count track for programs like Princess Captain's Circle.
   *  When set, the UI also counts in-app completed sailings and picks the better tier. */
  secondaryTrack?: 'sailings';
  secondaryTiers?: LoyaltyTier[];
  /** Generic string linking equivalent reward programs together (e.g. 'pointsChoice') */
  loyaltyCombinedGroup?: string;
  /** Whether this program supports legacy grandfathered tiers (e.g. Carnival Rewards) */
  supportsGrandfathering?: boolean;
  /** Length of the status qualifying cycle in years (e.g. 2 for Carnival Rewards) */
  cycleDurationYears?: number;
  /** End date for the active status qualifying cycle (e.g. '2028-12-31') */
  currentCycleEnd?: string;
}

export interface CruiseMetadata {
  loyaltyCalculationRules?: LoyaltyCalculationRules;
  loyaltyTiers?: LoyaltyTier[];
  loyaltyUnit?: string;
}

/**
 * Calculates loyalty points for a completed cruise based on the cruise line's rules.
 * Returns null if manual input is required or if calculation rules are not available.
 */
export const calculateCruiseLoyaltyPoints = (
  cruise: Cruise,
  cruiseMetadata: CruiseMetadata | null
): number | null => {
  if (!cruiseMetadata?.loyaltyCalculationRules) {
    return null;
  }

  const rules = cruiseMetadata.loyaltyCalculationRules;

  // If manual input is forced, return null
  if (rules.forceManualInput) {
    return null;
  }

  // Calculate cruise duration in days
  const cruiseDays = differenceInDays(
    parseISO(cruise.returnDate),
    parseISO(cruise.departureDate)
  );

  switch (rules.method) {
    case 'days': {
      // Simple days-based calculation
      const pointsPerDay = rules.pointsPerDay || 1;
      return cruiseDays * pointsPerDay;
    }

    case 'cabinTypeMultiplier': {
      // Points based on cabin type multipliers
      if (!rules.cabinTypeMultipliers) {
        return null;
      }
      
      const multiplier = rules.cabinTypeMultipliers[cruise.cabinType] || 1;
      const basePointsPerDay = rules.pointsPerDay || 1;
      return cruiseDays * basePointsPerDay * multiplier;
    }

    case 'sailings':
      // 1 credit per completed sailing, regardless of length (e.g. Disney Castaway Club)
      return 1;

    case 'manual':
      // Manual input required
      return null;

    default:
      return null;
  }
};

/**
 * Given a sailing count and an array of secondary tiers, returns the tier name
 * that the count falls into. Used for Princess Captain's Circle cruise-count track.
 */
export const getSecondaryTrackTierName = (
  sailingCount: number,
  secondaryTiers: LoyaltyTier[]
): string | null => {
  const tier = secondaryTiers.find(t => {
    const meetsMin = sailingCount >= t.min_value;
    const meetsMax = t.max_value === null || sailingCount <= t.max_value;
    return meetsMin && meetsMax;
  });
  return tier ? tier.name : null;
};

/**
 * Formats loyalty points for display
 */
export const formatLoyaltyPoints = (points: number): string => {
  return points.toLocaleString();
};

/**
 * Gets the default loyalty calculation method description
 */
export const getLoyaltyCalculationDescription = (rules: LoyaltyCalculationRules): string => {
  switch (rules.method) {
    case 'days':
      return `${rules.pointsPerDay || 1} point${(rules.pointsPerDay || 1) !== 1 ? 's' : ''} per day`;
    
    case 'cabinTypeMultiplier':
      return 'Points based on cabin type and cruise length';
    
    case 'sailings':
      return '1 credit per completed sailing';

    case 'manual':
      return 'Manual entry required';
    
    default:
      return 'Unknown calculation method';
  }
};

/**
 * Compares calculated tier (earned via points/stars) with a grandfathered tier
 * and returns the higher tier based on tier min_value.
 */
export const getEffectiveTierWithGrandfathering = (
  calculatedTierName: string | null,
  grandfatheredTier?: string,
  loyaltyTiers?: LoyaltyTier[]
): string | null => {
  if (!grandfatheredTier) return calculatedTierName;
  if (!calculatedTierName) return grandfatheredTier;
  if (!loyaltyTiers || loyaltyTiers.length === 0) return grandfatheredTier;

  const calculatedTier = loyaltyTiers.find(t => t.name.toLowerCase() === calculatedTierName.toLowerCase());
  const grandTier = loyaltyTiers.find(t => t.name.toLowerCase() === grandfatheredTier.toLowerCase());

  const calculatedMin = calculatedTier ? calculatedTier.min_value : 0;
  const grandMin = grandTier ? grandTier.min_value : 0;

  return grandMin >= calculatedMin ? grandfatheredTier : calculatedTierName;
};

/**
 * Formats a loyalty unit count (e.g. "1 Star", "250 Stars", "1 Point", "50 Points").
 */
export const formatLoyaltyUnit = (unit: string = 'points', count: number = 1): string => {
  const lower = unit.toLowerCase();
  if (lower === 'points' || lower === 'point') {
    return count === 1 ? 'Point' : 'Points';
  }
  if (lower === 'stars' || lower === 'star') {
    return count === 1 ? 'Star' : 'Stars';
  }
  if (lower === 'nights' || lower === 'night') {
    return count === 1 ? 'Night' : 'Nights';
  }
  return unit;
};

/**
 * Minimum starting Star balance for Grandfathered Platinum under Carnival Rewards
 * (10,000 Star biennial bonus boost).
 */
export const CARNIVAL_PLATINUM_BOOST_STARS = 10000;

/**
 * Checks whether a cruise line name represents Carnival.
 */
export const isCarnivalCruiseLine = (cruiseLineName?: string): boolean => {
  if (!cruiseLineName) return false;
  const lower = cruiseLineName.toLowerCase().trim();
  return lower.includes('carnival');
};

/**
 * Normalizes Carnival Rewards points for a user status record.
 * If the member is Grandfathered Platinum and the program is in Stars/manual mode,
 * guarantees that their active status balance is at least 10,000 Stars.
 */
export const getNormalizedLoyaltyStatusPoints = (
  cruiseLineName: string,
  rawStatus: number,
  grandfatheredTier?: string,
  unit?: string
): number => {
  const numStatus = Number(rawStatus) || 0;
  if (
    isCarnivalCruiseLine(cruiseLineName) &&
    grandfatheredTier === 'Platinum' &&
    (!unit || unit.toLowerCase().includes('star'))
  ) {
    return Math.max(CARNIVAL_PLATINUM_BOOST_STARS, numStatus);
  }
  return numStatus;
};